import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { stompClient } from '../websocket/stompClient';
import { useGameStore } from '../stores/gameStore';
import type { GameAction } from '../types';
import GameCanvas from '../components/GameCanvas';

export default function GamePage() {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const { currentMatch, gameState, isConnected } = useGameStore();
  const [chatMessage, setChatMessage] = useState('');
  const [chatMessages, setChatMessages] = useState<Array<{message: string, timestamp: number}>>([]);

  const { data: match, isLoading } = useQuery({
    queryKey: ['match', matchId],
    queryFn: () => apiClient.getMatch(Number(matchId)),
    enabled: !!matchId,
  });

  const startMatchMutation = useMutation({
    mutationFn: () => apiClient.startMatch(Number(matchId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['match', matchId] });
    },
  });

  const leaveMatchMutation = useMutation({
    mutationFn: () => apiClient.leaveMatch(Number(matchId)),
    onSuccess: () => {
      navigate('/lobby');
    },
  });

  useEffect(() => {
    if (match) {
      useGameStore.getState().setCurrentMatch(match);
    }
  }, [match]);

  useEffect(() => {
    if (!isConnected || !matchId) return;

    try {
      // Subscribe to game updates
      const gameSubscription = stompClient.subscribeToGame(
        Number(matchId),
        (message) => {
          const action: GameAction = JSON.parse(message.body);
          console.log('Game action received:', action);
          
          // Handle chat messages
          if (action.action === 'CHAT_MESSAGE' && action.payloadJson) {
            const payload = JSON.parse(action.payloadJson);
            setChatMessages(prev => [...prev, { message: payload.message, timestamp: Date.now() }]);
          }
        }
      );

      return () => {
        try {
          gameSubscription?.unsubscribe();
        } catch (error) {
          console.error('Error unsubscribing:', error);
        }
      };
    } catch (error) {
      console.error('Error subscribing to game:', error);
    }
  }, [isConnected, matchId]);

  const handleGameAction = (action: GameAction) => {
    if (!isConnected) return;
    
    const gameAction: GameAction = {
      ...action,
      matchId: Number(matchId),
      playerId: currentMatch?.players.find(p => p.userId === 1)?.id || 0, // TODO: Get actual player ID
    };
    
    stompClient.sendGameAction(gameAction);
  };

  const handleChatMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatMessage.trim()) return;

    handleGameAction({
      action: 'CHAT_MESSAGE',
      payloadJson: JSON.stringify({ message: chatMessage }),
      matchId: Number(matchId),
      playerId: 0, // TODO: Get actual player ID
    });

    setChatMessage('');
  };

  const handleDrawCard = () => {
    handleGameAction({
      action: 'DRAW_CARD',
      matchId: Number(matchId),
      playerId: 0, // TODO: Get actual player ID
    });
  };

  const handleEndTurn = () => {
    handleGameAction({
      action: 'END_TURN',
      matchId: Number(matchId),
      playerId: 0, // TODO: Get actual player ID
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading game...</div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Match not found</h3>
        <p className="mt-1 text-sm text-gray-500">The match you're looking for doesn't exist.</p>
        <button
          onClick={() => navigate('/lobby')}
          className="mt-4 btn-primary"
        >
          Back to Lobby
        </button>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Game Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{match.name}</h1>
            <p className="text-sm text-gray-500">
              {match.currentPlayers}/{match.maxPlayers} players • {match.status}
            </p>
          </div>
          <div className="flex space-x-3">
            {match.status === 'WAITING' && match.players.length >= 2 && (
              <button
                onClick={() => startMatchMutation.mutate()}
                className="btn-primary"
                disabled={startMatchMutation.isPending}
              >
                {startMatchMutation.isPending ? 'Starting...' : 'Start Game'}
              </button>
            )}
            <button
              onClick={() => leaveMatchMutation.mutate()}
              className="btn-secondary"
              disabled={leaveMatchMutation.isPending}
            >
              {leaveMatchMutation.isPending ? 'Leaving...' : 'Leave Match'}
            </button>
          </div>
        </div>
      </div>

      {/* Game Content */}
      <div className="flex-1 flex">
        {/* Game Canvas */}
        <div className="flex-1 bg-gray-100">
          <GameCanvas match={match} gameState={gameState} onGameAction={handleGameAction} />
        </div>

        {/* Game Controls */}
        <div className="w-80 bg-white border-l border-gray-200 flex flex-col">
          {/* Players List */}
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Players</h3>
            <div className="space-y-2">
              {match.players.map((player) => (
                <div
                  key={player.id}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded"
                >
                  <span className="text-sm font-medium text-gray-900">
                    {player.username}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    player.status === 'PLAYING' 
                      ? 'bg-green-100 text-green-800'
                      : player.status === 'READY'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {player.status}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Game Actions */}
          {match.status === 'IN_PROGRESS' && (
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Actions</h3>
              <div className="space-y-2">
                <button
                  onClick={handleDrawCard}
                  className="btn-primary w-full"
                >
                  Draw Card
                </button>
                <button
                  onClick={handleEndTurn}
                  className="btn-secondary w-full"
                >
                  End Turn
                </button>
              </div>
            </div>
          )}

          {/* Chat */}
          <div className="flex-1 flex flex-col p-4">
            <h3 className="text-lg font-medium text-white mb-3">Chat</h3>
            <div className="flex-1 bg-white/5 border border-white/10 rounded p-3 mb-3 overflow-y-auto max-h-64">
              {chatMessages.length > 0 ? (
                <div className="space-y-2">
                  {chatMessages.map((msg, idx) => (
                    <div key={idx} className="text-sm text-zinc-300">
                      <span className="text-cyan-400 font-semibold">Player:</span> {msg.message}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-zinc-500">Chat messages will appear here...</div>
              )}
            </div>
            <form onSubmit={handleChatMessage} className="flex space-x-2">
              <input
                type="text"
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 input-field"
              />
              <button type="submit" className="btn-primary">
                Send
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Connection Status */}
      {!isConnected && (
        <div className="bg-yellow-50 border-t border-yellow-200 px-6 py-2">
          <div className="flex items-center">
            <svg className="h-4 w-4 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-sm text-yellow-800">Connecting to game server...</span>
          </div>
        </div>
      )}
    </div>
  );
}
